import type { Step } from '@de/types/types';
import { HOST_ONLY_CONTEXT_KEYS } from '@de/utils/decisions/embeddedDecisions';

/** One step field that names a `flowContext` key only the page host fills. */
export type HostOnlyStepHandle = {
  readonly stepId: string;
  readonly field: 'id' | 'customField' | 'leadFieldAssociated';
  readonly handle: string;
};

const STEP_HANDLE_FIELDS = [
  'id',
  'customField',
  'leadFieldAssociated',
] as const;

function readNestedQuestions(step: Step): readonly unknown[] {
  const nested = (step as Step & { stepQuestions?: unknown }).stepQuestions;
  return Array.isArray(nested) ? nested : [];
}

function collect(step: unknown, found: HostOnlyStepHandle[]): void {
  if (typeof step !== 'object' || step === null) return;
  const record = step as Record<string, unknown>;
  const stepId = typeof record.id === 'string' ? record.id : '';
  for (const field of STEP_HANDLE_FIELDS) {
    const handle = record[field];
    if (typeof handle === 'string' && HOST_ONLY_CONTEXT_KEYS.has(handle)) {
      found.push({ stepId, field, handle });
    }
  }
}

/**
 * The step ids and authored handles that name a host-only `flowContext` key,
 * such as `decisions`, including those on a multi-question step's children.
 * `buildFlowContext` never writes these keys; the caller reports each one so
 * the author learns the handle was refused.
 */
export function findHostOnlyStepHandles(
  steps: readonly Step[] | undefined,
): readonly HostOnlyStepHandle[] {
  const found: HostOnlyStepHandle[] = [];
  for (const step of steps ?? []) {
    collect(step, found);
    if (step) {
      for (const child of readNestedQuestions(step)) collect(child, found);
    }
  }
  return found;
}
